import { PaginationProps, TableProps } from 'antd';
import { deleteApi, getApi } from 'apis';
import { SideDrawerWrapper } from 'components/Global';
import TableActionHeader from 'components/Global/TableActionHeader';
import ConfirmDelete from 'components/Global/confirmDeleteModel';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { toastText } from 'utils/utils';
import { useDebounce } from 'utils/hooks';
import AddWarningTagBody from './AddWarningTagBody';
import CustomTable from './CustomTable';


const WarningTagsTable = () => {

	// State variables
	const [drawerAnimation, setDrawerAnimation] = useState<boolean>(false);
	const [isSideDrawerOpen, setSideDrawerOpen] = useState<boolean>(false);
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(10);
	const [filter, setFilter] = useState('');
	const [sort, setSort] = useState('');
	const [type, setType] = useState('asc');
	const [drawerInfo, setDrawerInfo] = useState({
		drawerTitle: 'Warning Tag',
	});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editSelectedWarningTag, setEditSelectedWarningTag] = useState<any>();
	const [totalWarningTag, setTotalWarningTag] = useState(0);
	const [warningTagData, setWarningTagData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
	const [isSearchCancel, setIsSearchCancel] = useState<boolean>(false);

	const debouncedSearch = useDebounce(search, 500);

	const removeDrawerFromDom = () => {
		setSideDrawerOpen(false);
	};

	const openDrawerHandler = () => {
		setDrawerInfo({ drawerTitle: 'Add Warning Tag' });
		setDrawerAnimation(true);
		setSideDrawerOpen(true);
	};


	const closeDrawerByAnimation = () => {
		setEditSelectedWarningTag(undefined); //!isAddUserLoading &&
		setDrawerAnimation(false); //!isAddUserLoading &&
	};


	const performSearchHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setSearch(value);
		setPage(1);
		if (value.length > 0) {
			setIsSearchCancel(true)
		} else {
			setIsSearchCancel(false)
		}
	};


	const showModal = () => {
		setIsModalOpen(true);
	};

	const setDrawerInfoHandler = (drawerTitle: any) => {
		setDrawerInfo({ drawerTitle });
	};

	const handleOk = () => {
		setIsModalOpen(false);
		setEditSelectedWarningTag(null);
	};

	const handleCancel = () => {
		setIsModalOpen(false); // !isAddUserLoading &&
		setEditSelectedWarningTag(null);
	};

	const deleteHandler = async () => {
		if (editSelectedWarningTag) {
			try {
				setIsDeleteLoading(true);
				const resp = await deleteApi(`/warning-tags/delete/${editSelectedWarningTag?.id}`);
				toastText(resp?.data?.message ?? 'warning-tags deleted successfully', 'success');
			} catch (error: any) {
				toastText(error.response?.data?.message ?? 'Something went wrong in delete warning-tags', 'error');
			} finally {
				setIsDeleteLoading(false);
				handleCancel();
				fetchWarningTags();
			}
		}
	};

	const fetchWarningTags = useCallback(async () => {
		setIsLoading(true);
		const query = {
			search: debouncedSearch,
			sort: sort,
			type: type,
			filter: filter
		};
		try {
			const res = await getApi('/warning-tags/get-all', query);
			setWarningTagData(res?.data?.data);
			setTotalWarningTag(res?.data?.total);
		} catch (error: any) {
			toastText(error?.response?.data?.error?.errorDescription, 'error');
		} finally {
			setIsLoading(false);
		}

	}, [debouncedSearch, type, sort, filter]);

	const handleTableChange: TableProps<any>['onChange'] = (
		pagination,
		filters,
		sorter
	) => {
		const sortOrder = Array.isArray(sorter)
			? sorter[0]?.order
			: sorter.order === 'ascend'
				? 'asc'
				: 'desc';
		setType(sortOrder as string);

		const sortField = Array.isArray(sorter)
			? sorter[0]?.field
			: sorter.field === 'name'
				? 'firstName'
				: sorter.field;
		setSort(sortField as string);
	};

	const onShowSizeChange: PaginationProps['onShowSizeChange'] = (
		current,
		pageSize
	) => {
		setPage(current);
		setLimit(pageSize);
	};


	useEffect(() => {
		fetchWarningTags();
	}, [debouncedSearch, sort, type, filter, fetchWarningTags]);

	// JSX
	return (
		<>
			<div className={''}>
				<TableActionHeader>
				</TableActionHeader>

				<CustomTable
					dataSource={warningTagData}
					handleTableChange={handleTableChange}
					onShowSizeChange={onShowSizeChange}
					isLoading={isLoading}
					search={search}
					totalRecords={totalWarningTag}
					performSearchHandler={performSearchHandler}
					performFilterHandler={(value: any) => setFilter(value)}
					openDrawerHandler={openDrawerHandler}
					setDrawerInfoHandler={setDrawerInfoHandler}
					setEditSelectedWarningTag={setEditSelectedWarningTag}
					showModal={showModal}
					currentPage={page}
					pageSize={limit}
					isSearchCancel={isSearchCancel}
					setIsSearchCancel={setIsSearchCancel}
					setSearch={setSearch}
				/>
			</div>
			<ConfirmDelete
				handleCancel={handleCancel}
				handleOk={handleOk}
				isModalOpen={isModalOpen}
				deleteHandler={deleteHandler}
				isLoading={isDeleteLoading}
				selectedDeleteData={editSelectedWarningTag?.tagName}
				deleteHeading="Warning Tag"
			/>
			{isSideDrawerOpen && (
				<SideDrawerWrapper
					isOpen={drawerAnimation}
					removeDrawerFromDom={removeDrawerFromDom}
					closeDrawerByAnimation={closeDrawerByAnimation}
					headerTitle={drawerInfo.drawerTitle}
					position="right"
					width="half"
				>
					<AddWarningTagBody
						closeDrawerByAnimation={closeDrawerByAnimation}
						editSelectedWarningTag={editSelectedWarningTag}
						fetchWarningTags={fetchWarningTags}
					/>
				</SideDrawerWrapper>
			)}
		</>
	);
};

export default WarningTagsTable;
